import { useCallback } from 'react'

/**
 * Custom hook for keyboard navigation in selection grids
 * Handles arrow up/down keys to navigate between selectable items
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.data - Array of items to navigate
 * @param {Object} options.selectedItem - Currently selected item
 * @param {string} options.keyField - Field name used as unique identifier (e.g., 'lineNum')
 * @param {Function} options.onSelect - Callback when selection changes
 * @param {Function} [options.isSelectable] - Optional filter function to determine if item is selectable
 * @returns {Function} handleKeyDown - Event handler for keyboard events
 *
 * @example
 * const handleKeyDown = useKeyboardNavigation({
 *     data: memberships,
 *     selectedItem: selectedMembership,
 *     keyField: 'lineNum',
 *     onSelect: onMembershipSelect,
 *     isSelectable: isMembershipSelectable
 * })
 */
export const useKeyboardNavigation = ({
    data,
    selectedItem,
    keyField,
    onSelect,
    isSelectable = () => true
}) => {
    const handleKeyDown = useCallback((e) => {
        if (!data?.length || !selectedItem) return

        // Filter to only selectable items
        const selectableItems = data.filter(isSelectable)
        if (selectableItems.length === 0) return

        const currentIndex = selectableItems.findIndex(
            item => item[keyField] === selectedItem[keyField]
        )
        if (currentIndex === -1) return

        if (e.key === 'ArrowDown') {
            e.preventDefault()
            const nextIndex = Math.min(currentIndex + 1, selectableItems.length - 1)
            if (nextIndex !== currentIndex) {
                onSelect(selectableItems[nextIndex])
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            const prevIndex = Math.max(currentIndex - 1, 0)
            if (prevIndex !== currentIndex) {
                onSelect(selectableItems[prevIndex])
            }
        }
    }, [data, selectedItem, keyField, onSelect, isSelectable])

    return handleKeyDown
}
